import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { GlobeAltIcon, HeartIcon, TruckIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';

/**
 * Owner, 24 Sep: "Motor, Health and Travel" -- and, in the same breath, the
 * platform's own site does NOT quote. Quoting happens only from a BROKER's own
 * pages, so the three options live here, on `/public/quote/:brokerSlug`, and
 * each tile goes to that broker's form for the line:
 *
 *   /public/quote/<slug>/motor   -> PublicQuoteJourney (registration -> live price)
 *   /public/quote/<slug>/health  -> PublicLineQuoteJourney line="health"
 *   /public/quote/<slug>/travel  -> PublicLineQuoteJourney line="travel"
 *
 * The SLUG-LESS routes (`/quote`, `/quote/<line>`, `/public/quote`) are for a
 * BROKER's custom domain: `HostQuoteGate` asks `GET /api/public/quotes/site/`
 * whose site this is and draws the same chooser and forms for that broker
 * (the forms send no slug; the server resolves the domain). On the platform's
 * own host that answer is a 404, and the gate draws `PlatformQuoteNotice` --
 * no form. The API agrees: on the platform host with no slug it resolves no
 * broker (`apps.microsite.resolution.resolve_public_quote_broker`).
 */

export type QuoteLine = 'motor' | 'health' | 'travel';

export const QUOTE_LINES: {
  line: QuoteLine;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    line: 'motor',
    title: 'Motor',
    description: 'Car or two-wheeler. Enter your registration number and see live premiums.',
    icon: TruckIcon,
  },
  {
    line: 'health',
    title: 'Health',
    description: 'Cover for you, your family or your parents.',
    icon: HeartIcon,
  },
  {
    line: 'travel',
    title: 'Travel',
    description: 'A single trip, or a year of trips abroad.',
    icon: GlobeAltIcon,
  },
];

/** Where this broker's quote pages live: under their slug, or -- on their
 *  own custom domain, with no slug -- at `/quote`. */
export function quoteBasePath(brokerSlug: string | undefined): string {
  return brokerSlug ? `/public/quote/${encodeURIComponent(brokerSlug)}` : '/quote';
}

export function quoteLinePath(brokerSlug: string | undefined, line: QuoteLine): string {
  return `${quoteBasePath(brokerSlug)}/${line}`;
}

type BrokerLookup = { status: 'loading' } | { status: 'missing' } | { status: 'ok'; name: string | null };

/**
 * Is the broker this slug names publicly live? (Pre-deploy review, 24 Sep.)
 *
 * A dead slug -- a typo, a suspended broker, a closed public profile -- used to
 * draw the three tiles and a whole form, which the server then refused on
 * submit. `GET /api/public/brokers/<slug>/` 404s for exactly those brokers, so
 * a 404 draws `PlatformQuoteNotice` instead. Any OTHER failure is not proof the
 * broker is gone and does not block the page; the server still decides on
 * every call the forms make.
 */
export function useBrokerBySlug(slug: string | undefined): BrokerLookup {
  const [lookup, setLookup] = useState<BrokerLookup>(slug ? { status: 'loading' } : { status: 'ok', name: null });
  useEffect(() => {
    if (!slug) {
      setLookup({ status: 'ok', name: null });
      return;
    }
    let cancelled = false;
    setLookup({ status: 'loading' });
    axios
      .get<{ name?: string }>(`/policy/api/public/brokers/${encodeURIComponent(slug)}/`)
      .then((res) => {
        if (!cancelled) setLookup({ status: 'ok', name: typeof res.data?.name === 'string' ? res.data.name : null });
      })
      .catch((err: { response?: { status?: number } }) => {
        if (cancelled) return;
        setLookup(err?.response?.status === 404 ? { status: 'missing' } : { status: 'ok', name: null });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return lookup;
}

function PageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900" role="status">
      <LoadingSpinner />
    </div>
  );
}

/** The slugged form routes: a dead slug draws the notice, not a form. */
export function BrokerSlugGate({ children }: { children: React.ReactNode }) {
  const { brokerSlug } = useParams<{ brokerSlug: string }>();
  const broker = useBrokerBySlug(brokerSlug);
  if (broker.status === 'loading') return <PageSpinner />;
  if (broker.status === 'missing') return <PlatformQuoteNotice />;
  return <>{children}</>;
}

export function PublicQuoteChooser({ hostBrokerName }: { hostBrokerName?: string } = {}) {
  const { brokerSlug = '' } = useParams<{ brokerSlug: string }>();
  const broker = useBrokerBySlug(brokerSlug || undefined);

  if (broker.status === 'loading') return <PageSpinner />;
  if (broker.status === 'missing') return <PlatformQuoteNotice />;
  const brokerName = hostBrokerName ?? broker.name;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900 sm:px-6">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 text-center">
          {brokerName && (
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
              {brokerName}
            </p>
          )}
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">Get a quote</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">What would you like to insure?</p>
        </header>

        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {QUOTE_LINES.map(({ line, title, description, icon: Icon }) => (
            <li key={line}>
              <Link
                to={quoteLinePath(brokerSlug, line)}
                data-testid={`quote-tile-${line}`}
                className="flex h-full flex-col items-center rounded-2xl border-2 border-transparent bg-white p-6 text-center shadow-sm transition-colors hover:border-primary-300 focus:outline-none focus-visible:border-primary-500 dark:bg-gray-800 dark:hover:border-primary-700"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                  <Icon className="h-8 w-8" aria-hidden="true" />
                </span>
                <span className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{title}</span>
                <span className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * The slug-less quote routes: a broker's custom domain gets that broker's
 * pages, the platform host gets `PlatformQuoteNotice`. Decided by the server
 * (`GET /api/public/quotes/site/`), never guessed from the hostname here.
 */
export function HostQuoteGate({ children }: { children: (brokerName: string) => React.ReactNode }) {
  const [site, setSite] = useState<{ status: 'loading' } | { status: 'none' } | { status: 'broker'; name: string }>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    axios
      .get<{ broker_name?: string }>('/policy/api/public/quotes/site/')
      .then((res) => {
        if (cancelled) return;
        const name = res.data?.broker_name;
        setSite(typeof name === 'string' && name ? { status: 'broker', name } : { status: 'none' });
      })
      .catch(() => {
        if (!cancelled) setSite({ status: 'none' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (site.status === 'loading') return <PageSpinner />;
  if (site.status === 'none') return <PlatformQuoteNotice />;
  return <>{children(site.name)}</>;
}

/**
 * The slug-less quote routes on the platform host. The platform does not quote
 * on its own; a visitor is sent to find their broker. Deliberately no form: the
 * API would refuse it anyway.
 */
export function PlatformQuoteNotice() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900 sm:px-6">
      <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-gray-800">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Get a quote from your broker</h1>
        <p className="mt-3 text-gray-600 dark:text-gray-400">
          Quotes are prepared by insurance brokers. Please start your quote from your broker&rsquo;s
          own page.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/brokers-directory"
            className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Find a broker
          </Link>
          <Link
            to="/"
            className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
